import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import copy from 'copy-to-clipboard';
import { enqueueSnackbar, SNACKBAR_VARIANTS } from 'redux_store';

/**
 * Single-source-of-truth clipboard write hook.
 *
 * Returns a function `(text, opts?) => Promise<boolean>`. Dispatches a
 * bottom-right snackbar with the success / failure copy from i18n on
 * each write. Override the messages per-call via `successMessage` /
 * `errorMessage` if a non-default phrasing is needed.
 *
 * The underlying package (`copy-to-clipboard`) is hidden by this
 * wrapper. To switch packages later, change this file only.
 *
 * Usage:
 *   const copyToClipboard = useCopyToClipboard();
 *   const handleCopy = async () => { await copyToClipboard(link); };
 */
const useCopyToClipboard = () => {
  const dispatch = useDispatch();
  const { notifications } = useSelector(state => state.i18n);

  return useCallback(
    async (text, { successMessage, errorMessage } = {}) => {
      const ok = copy(text ?? '');
      const variant = ok ? SNACKBAR_VARIANTS.success : SNACKBAR_VARIANTS.error;
      const message = ok
        ? successMessage ?? notifications.dataCopied
        : errorMessage ?? notifications.dataNotCopied;
      dispatch(
        enqueueSnackbar({
          message,
          options: {
            variant,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          },
        }),
      );
      return ok;
    },
    [dispatch, notifications.dataCopied, notifications.dataNotCopied],
  );
};

export default useCopyToClipboard;
